import React, { useState, useEffect, useRef, FC, Fragment } from 'react';
import styled, { css } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp, faPipe, faSearch, faXmark } from '@fortawesome/pro-solid-svg-icons';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { colorStack } from '../../../styleHelpers/colors';
import { useAlert, useDropdown } from '../../../tools/hooks';
import { useOrganization } from '../../../tools/organizationHooks';
import { isLegalEntityAddressMandatory } from '../../../tools/legalEntityCreationVariant';
import { isNoEmailProfileCreationEnabled } from '../../../tools/profileCreationVariant';
import { NewCompanyForm } from './Components/NewCompanyForm';
import { NewUserForm } from './Components/NewUserForm';
import { LabelWithEllipsis } from '../Label/Label';
import { MandatoryFieldStar } from '../MandatoryFieldStar/MandatoryFieldStar';
import { EProfileType, EFilterType } from '../../../entities/IGlobal';
import { IValue } from '../../../entities/IPickers';
import { EPillColorSchema, EPillShape, Pill } from '../Pill/Pill';
import { ResultBox } from '../ResultsBox/ResultBox';
import { IItem } from '../../../entities/IFilters';
import { Badge, EBadgeColor } from '../Badge/Badge';
import { EHintType, Hint } from '../Hint/Hint';
import { SearchResults } from './Components/SearchResults';
import { checkUserInHrApi } from '../../../actions/profileActions';
import IntlMessage, { useIntlMessage } from '../IntlMessage';
import { newUpdateCompany } from '../../../actions/legalEntitiesActions';
import { selectSimpleLegalEntityFormEnabled } from '../../../tools/platformConfiguration';
import { EAlertType } from '../../../entities/IAlert';
import { isValidEmail } from '../../../tools/emailChecker';
import { resolvePickerProfileType } from '../../../tools/pickerProfileType';
import { getPickerPillPicture, getPickerPillText } from '../../../tools/pickerPillText';
import {
    estimatePickerDropdownHeight,
    findPickerClippingAncestor,
    fitPickerResultsHeight,
    resolvePickerClipBounds,
    shouldOpenPickerDropdownUpwards
} from '../../../tools/pickerDropdownPlacement';
import { ESpinnerSize, Spinner } from '../Spinner/Spinner';

type CheckUserInHrApi = ReturnType<typeof checkUserInHrApi>;
type NewUpdateCompany = ReturnType<typeof newUpdateCompany>;


const Wrapper = styled.div`
    position: relative;
    width: 100%;
    border-radius: 4px;
`;
const SearchResult = styled.div<{ openDropdownUpwards: boolean }>`
    width: 100%;
    position: absolute;
    left: 0;
    top: ${props => (props.openDropdownUpwards ? 'auto' : '100%')};
    bottom: ${props => (props.openDropdownUpwards ? '100%' : 'auto')};
    z-index: 99999;
`;

const WrapperContainer = styled.div`
    display: flex;
    align-items: center;
    gap: .5rem;
    width: 100%;
    position: relative;
`;

const TextWrapper = styled.div<{ selected: boolean; multiSelect: boolean; error: boolean; disabled: boolean, expand: boolean; noIcon: boolean }>`
    position: relative;
    display: flex;
    width: 100%;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    color: ${colorStack.content};
    height: 40px;
    min-height: 40px;
    background: ${colorStack.white};
    border: 2px solid ${colorStack.ligthGrey};
    border-radius: 8px;
    ${props => props.error && css`
        border: 2px solid ${colorStack.middleRed};
    `}
    ${props => props.disabled && css`
        background: ${colorStack.bodyBg};
    `}
    ${({ expand }) => (expand) && css`
        height: auto;
    `}
    ${props => props.noIcon && css`
        padding: 0;
    `}
    :hover {
        border: 2px solid ${colorStack.middleBlue};
        ${props => props.disabled && css`
            border: 2px solid ${colorStack.ligthGrey};
        `}
    }
`;

const SelectedElemsList = styled.div<{ expand: boolean; disabled: boolean; leftIcon: boolean; }>`
    padding: 0 .5rem 0 0;
    max-width: 100%;
    width: 100%;
    display: flex;
    ${props => props.expand && css`
        height: auto;
        margin-bottom: .5rem;
    `}
    ${props => props.expand && css`
        margin-bottom: 0;
    `}
    ${props => props.leftIcon && css`
        padding-left: 12px;
    `}
`;

const RightIcons = styled.div`
    display: flex;
    align-items: center;
    gap: .5rem;
    position: absolute;
    right: 0.5rem;
    top: 0;
    bottom: 0;
    margin: auto;
    > svg {
        cursor: pointer;
    }
`;

const Content = styled.div<{ expand: boolean; disabled: boolean; noIcon: boolean; }>`
    height: 100%;
    width: calc(100% - 16px); /* 16px is for left icons */
    display: flex;
    align-items: center;
    padding: 2px .5rem 0 1.5rem;
    ${props => props.expand && css`
        flex-direction: column;
    `}
    > div:nth-child(2) {
        cursor: pointer;
    }
    ${props => props.expand && css`
        padding: 2px .5rem .5rem 1.5rem;
    `}
    ${props => props.noIcon && css`
        padding: 2px .5rem 0 0;
        width: 100%;
    `}
`;

const InputObject = styled.input<{ expand: boolean; }>`
    border: none!important;
    outline: none!important;
    width: auto !important;
    min-width: 104px!important;
    flex-grow: 1;
    font-weight: 400!important;
    font-size: 16px!important;
    line-height: 16px!important;
    &:hover {
        border: none!important;
        outline: none!important;
    }
    ${props => props.expand && css`
        font-weight: 400!important;
        min-height: 32px;
        padding: 0 0 0 0.5rem;
    `}
    &::placeholder {
        color: ${colorStack.middleGrey}!important;
        font-weight: 400!important;
        font-size: 16px!important;
        line-height: 16px!important;
        font-family: 'Roboto', sans-serif;
        &:disabled {
            color: ${colorStack.middleGrey}!important;
        }
    }
`;

const ScrollBox = styled.div<{ expand: boolean; numberOfLines: number }>`
    display: flex;
    padding: 0 .5rem;
    align-items: center;
    padding: 0 52px 0 .5rem;
    width: 100%;
    height: 32px;
    ${props => props.expand && css`
        height: auto;
    `}
`;

const InnerBox = styled.div<{ expand?: boolean; fullwidth: boolean; multiSelect: boolean }>`
    display: flex;
    align-items: center;
    gap: 4px;
    width: calc(100% - 52px);
    flex-wrap: wrap;
    overflow: hidden;
    height: 32px;
    ${props => props.expand && css`
        overflow: visible;
        height: auto;
    `}
    ${props => props.fullwidth && css`
        width: 100%;
    `}

    ${props => !props.multiSelect && css`
        > div {
            width: fit-content;
            max-width: 100%;
            width: 100%;
        }
    `};
    input[type=text] {
        padding: 0!important;
        font-weight: 400;
    }
`;

const SearchIcon = styled.div`
    position: absolute;
    left: 1rem;
    cursor: pointer;
`;

export enum EButtonsTypes {
    Person = 'Person',
    Company = 'Company',
    Theme = 'Theme',
    ClientReference = 'ClientReference',
    AreaOfResponsibility = 'AreaOfResponsibility',
    Invite = 'Invite',
    All = 'All'
}

export interface IPickerProps {
    value?: IValue[];
    loading?: boolean;
    parentRef?: React.RefObject<HTMLDivElement>;
    placeholder?: string;
    errorMsg?: string | React.ReactNode;
    disabled?: boolean;
    multiSelect?: boolean;
    pillColorSchema?: EPillColorSchema;
    hideButtons?: boolean;
    onlyDataFromForm?: boolean;
    label?: React.ReactNode;
    required?: boolean;
    data?: IValue[];
    buttonsType?: EButtonsTypes;
    oldVersion?: boolean;
    resultsMaxHeight?: number;
    fitResultsToAvailableSpace?: boolean;
    onSelectElement?(value: IValue[]);
    onSelectElementMulti?(value: IValue[]);
    getData?(query?: string, page?: number): Promise<IValue[]>;
    enableInfiniteScroll?: boolean;
    infiniteScrollPageSize?: number;
    createTheme?(inputValue: string): Promise<IValue>;
    createClientReference?(inputValue: string): Promise<IValue>;
    createAreaOfResponsibility?(inputValue: string): Promise<IValue>;
    outsideCreateUser?(searchValue?: string): void;
    getDisabledItemReason?(data: any): string | undefined;
    renderItemDetails?(data: any): React.ReactNode;
    renderItemAside?(data: any): React.ReactNode;
    renderItemHeader?(data: any): React.ReactNode;
}

let debounceTimeout: ReturnType<typeof setTimeout>;

export const CorePicker: FC<IPickerProps> = props => {
    const { intlFormatMessage } = useIntlMessage();
    const dispatch = useDispatch();
    const simpleLegalEntityForm = useSelector(selectSimpleLegalEntityFormEnabled);
    const addAlert = useAlert();
    const { organization } = useParams<{ organization: string }>();
    const { currentOrganization } = useOrganization(organization);
    const isAddressMandatory = isLegalEntityAddressMandatory(currentOrganization);
    const allowEmptyEmail = isNoEmailProfileCreationEnabled(currentOrganization, props.onlyDataFromForm);
    const selectedElemetsListRef = useRef<HTMLDivElement>();
    const selectedElemetsListInnerRef = useRef<HTMLDivElement>();
    const [wrapperRef, dropdownOpen, __, closeDropdown, openDropdown] = useDropdown(true);
    const [searchResults, setSearchResults] = useState<IValue[]>([]);
    const [showCompanyForm, setshowCompanyForm] = useState<boolean>(false);
    const [showUserForm, setshowUserForm] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [hasMoreResults, setHasMoreResults] = useState<boolean>(false);
    const searchPageRef = useRef(0);
    const loadingMoreRef = useRef(false);
    const hasMoreResultsRef = useRef(false);
    const [inputValue, setInputValue] = useState<string>('');
    const [selectedElements, setSelectedElements] = useState<IValue[]>([]);
    const [selectedItems, setSelectedItems] = useState<IItem[]>([]);
    const [expand, setExpand] = useState<boolean>(false);
    const [openDropdownUpwards, setOpenDropdownUpwards] = useState<boolean>(false);
    const [fittedResultsMaxHeight, setFittedResultsMaxHeight] = useState<number>(undefined);
    const [reRender, setReRender] = useState<boolean>(false);
    const [hrApiData, setHrApiData] = useState<{
        firstName: string;
        lastName: string;
        email: string;
    }>(undefined);
    const [formAnchorWidth, setFormAnchorWidth] = useState<number | undefined>(undefined);

    useEffect(() => {
        if ((showCompanyForm || showUserForm) && wrapperRef.current) {
            setFormAnchorWidth(wrapperRef.current.getBoundingClientRect().width);
        } else {
            setFormAnchorWidth(undefined);
        }
    }, [showCompanyForm, showUserForm]);

    useEffect(() => {
        if (selectedElemetsListInnerRef.current) {
            setTimeout(() => {
                setReRender(true);
            }, 10); // Timeout to ensure the ref is updated before measuring
        }
    });

    useEffect(() => {
        if (props.multiSelect && selectedElements?.length > 0 && selectedElemetsListInnerRef?.current?.getBoundingClientRect().height > 35) {
            setExpand(true);
        }
    }, [selectedElements]);

    useEffect(() => {
        if (!dropdownOpen && !props.value && showUserForm) {
            setInputValue('');
        }
        if (!dropdownOpen) {
            setExpand(false);
        }
        if (props.oldVersion && !dropdownOpen) {
            setSelectedItems(selectedElements.map(elem => ({
                data: elem.data,
                id: elem.key,
                text: elem.text,
                translationKey: '',
                visible: true
            })));
        }
    }, [dropdownOpen]);

    useEffect(() => {
        const nextValue = _.uniqBy((props.value?.filter(elem => elem !== null) || []), 'key');
        const hasValueChanged =
            nextValue.length !== selectedElements.length
            || nextValue.some((elem, index) => {
                const prev = selectedElements[index];
                const prevPicture = prev?.data?.picture || prev?.data?.Picture;
                const nextPicture = elem?.data?.picture || elem?.data?.Picture;
                return prev?.key !== elem?.key
                    || prev?.text !== elem?.text
                    || prevPicture !== nextPicture
                    || prev?.data?.type !== elem?.data?.type;
            });

        if (nextValue.length > 0 && hasValueChanged) {
            setSelectedElements(nextValue);
            setSelectedItems(nextValue.map(elem => ({
                visible: true,
                text: elem.text,
                id: elem.key,
                data: elem.data,
                translationKey: ''
            })));
        }

        if (nextValue.length === 0 && selectedElements.length > 0) {
            setSelectedElements([]);
            setSelectedItems([]);
        }
    }, [props.value, selectedElements]);

    const getData = async (text: string, page: number = 0) => {
        const pageSize = props.infiniteScrollPageSize ?? 10;
        const isLoadMore = page > 0;

        if (isLoadMore) {
            if (!props.enableInfiniteScroll || loadingMoreRef.current || !hasMoreResultsRef.current) {
                return;
            }
            loadingMoreRef.current = true;
        } else {
            setLoading(true);
            setSearchResults([]);
            hasMoreResultsRef.current = false;
            setHasMoreResults(false);
            searchPageRef.current = 0;
        }

        try {
            const results = (await props.getData?.(text, page)) ?? [];
            if (isLoadMore) {
                setSearchResults(prev => _.uniqBy([...(prev || []), ...results], 'key'));
            } else {
                setSearchResults(results);
            }
            searchPageRef.current = page;
            const moreAvailable = !!props.enableInfiniteScroll && results.length >= pageSize;
            hasMoreResultsRef.current = moreAvailable;
            setHasMoreResults(moreAvailable);
        } finally {
            if (isLoadMore) {
                loadingMoreRef.current = false;
            } else {
                setLoading(false);
            }
        }
    };

    const loadMoreResults = () => {
        getData(inputValue || '', searchPageRef.current + 1);
    };

    const selectElement = (e: React.MouseEvent<HTMLButtonElement>, item: IValue, isRemove?: boolean) => {
        e && closeDropdown();
        e && setExpand(false);
        setSelectedItems(state => isRemove ? state.filter(elem => elem.id !== item.data.id) : _.uniqBy([...state, { text: item.text, id: item.key, data: item, visible: true, translationKey: '' }], (elem) => elem.id));
        setSelectedElements(state => isRemove ? state.filter(elem => elem.key !== item.data.id) : _.uniqBy([...state, item], (elem) => elem.key));
        setTimeout(() => {
            props.multiSelect ?
                props.onSelectElementMulti(isRemove ? selectedElements.filter(elem => elem.key !== item.data.id) : _.uniqBy([...selectedElements, item], (elem) => elem.key))
                :
                props.onSelectElement(isRemove ? selectedElements.filter(elem => elem.key !== item.data.id) : _.uniqBy([...selectedElements, item], (elem) => elem.key));
        }, 100);
        setSearchResults([]);
        setInputValue('');
        getData('');
    };

    const companyFormHandler = () => {
        if (simpleLegalEntityForm) {
            setshowCompanyForm(false);
            const newEntity = {
                name: inputValue
            };
            dispatch<NewUpdateCompany>(newUpdateCompany(newEntity)).then(res => {
                selectElement(undefined, {
                    key: res.id,
                    text: res.name,
                    data: { ...res, avatarType: EProfileType.LegalEntity, type: EProfileType.LegalEntity }
                });
                addAlert(<IntlMessage id="searchInput.alert.company" />, EAlertType.SUCCESS);
            }).catch(err => {
                addAlert(err, EAlertType.ERROR);
            });
        } else {
            setshowCompanyForm(state => !state);
        }
        closeDropdown();
        setExpand(false);
    };

    const openUserForm = () => {
        setshowUserForm(state => !state);
        closeDropdown();
        setExpand(false);
    };

    const userFormHandler = () => {
        if (props.outsideCreateUser) {
            const normalizedSearchValue = typeof inputValue === 'string' ? inputValue.trim() : undefined;
            props.outsideCreateUser(normalizedSearchValue || undefined);
            return;
        }
        dispatch<CheckUserInHrApi>(checkUserInHrApi(inputValue || '')).then((res) => {
            if (res?.items?.length > 0) {
                setHrApiData({
                    firstName: res.items[0].firstName,
                    lastName: res.items[0].lastName,
                    email: res.items[0].emailContact
                });
            }
            openUserForm();
        }).catch(() => {
            openUserForm();
        });
    };

    const closeUserRofmHandler = () => {
        setHrApiData(undefined);
        setshowUserForm(state => !state);
        closeDropdown();
        setExpand(false);
    };

    const themeFormHandler = () => {
        props.createTheme(inputValue).then((themeValue: IValue) => {
            setInputValue('');
            selectElement(undefined, themeValue);
        });
    };

    const clientReferenceFormHandler = () => {
        props.createClientReference(inputValue).then((clientReferenceValue: IValue) => {
            setInputValue('');
            selectElement(undefined, clientReferenceValue);
        }).catch(() => undefined);
    };

    const areaOfResponsibilityFormHandler = () => {
        props.createAreaOfResponsibility(inputValue).then((areaOfResponsibilityValue: IValue) => {
            setInputValue('');
            selectElement(undefined, areaOfResponsibilityValue);
        });
    };

    const selectItemsHandler = (item: IItem) => {
        const existItem = !!selectedItems.find(elem => elem.id === item.id);
        const finalData = [item].map(elem => ({
            key: elem.id,
            text: elem.text,
            data: { ...elem.data }
        }));
        if (props.multiSelect) {
            setSelectedItems(state => state.find(elem => elem.id === item.id) ? state.filter(elem => elem.id !== item.id) : [...state, item]);
            setSelectedElements(state => existItem ? state.filter(elem => elem.key !== item.id) : [...state, ...finalData]);
            props.onSelectElementMulti(existItem ? selectedElements.filter(elem => elem.key !== item.id) : [...selectedElements, ...finalData]);
        } else {
            setSelectedItems(state => state.find(elem => elem.id === item.id) ? state.filter(elem => elem.id !== item.id) : [item]);
            setSelectedElements(state => existItem ? state.filter(elem => elem.key !== item.id) : (props.multiSelect ? [...state, ...finalData] : finalData));
            props.onSelectElement(existItem ? selectedElements.filter(elem => elem.key !== item.id) : finalData);
            closeDropdown();
            setExpand(false);
        }
    };

    const autoSaveSelectItemsHandler = (item: IItem) => {
        const existItem = !!selectedItems.find(elem => elem.id === item.id);
        const finalData = [item].map(elem => ({
            key: elem.id,
            text: elem.text,
            data: { ...elem.data }
        }));
        setSelectedItems(state => existItem ? state.filter(elem => elem.id !== item.id) : (props.multiSelect ? [...state, item] : [item]));
        setSelectedElements(state => existItem ? state.filter(elem => elem.key !== item.id) : (props.multiSelect ? [...state, ...finalData] : finalData));
        props.multiSelect ?
            props.onSelectElementMulti(existItem ? selectedElements.filter(elem => elem.key !== item.id) : [...selectedElements, ...finalData])
            :
            props.onSelectElement(existItem ? selectedElements.filter(elem => elem.key !== item.id) : finalData);
        setInputValue('');
        getData('');
        if (!props.multiSelect) {
            closeDropdown();
            setExpand(false);
        }
    };

    const removeElement = (value: string) => {
        if (!props.disabled) {
            setSelectedItems(state => state.filter(elem => elem.id !== value));
            selectElement(undefined, {
                key: '',
                text: '',
                data: {
                    id: value
                }
            },
                true);
            setSelectedElements(state => state.filter(elem => elem.key !== value));
        }
    };

    const onClick = () => {
        if (!showCompanyForm && !showUserForm) {
            if (props.multiSelect && selectedElements?.length > 0) {
                setExpand(true);
            }
            if (selectedElements?.length === 0 || props.multiSelect) {
                openDropdown();
            }
            if (!dropdownOpen) {
                getData(inputValue || '');
            }
        }
    };

    const clearAllSelectedItems = () => {
        if (!props.disabled) {
            closeDropdown();
            setExpand(false);
            setSelectedItems([]);
            setSelectedElements([]);
            props.multiSelect ? props.onSelectElementMulti([]) : props.onSelectElement([]);
            setInputValue('');
            getData('');
        }
    };

    const saveHandler = () => {
        const finalData = selectedItems.map(elem => ({
            key: elem.id,
            text: elem.text,
            data: { ...elem.data }
        }));
        setSelectedElements(finalData);
        closeDropdown();
        setExpand(false);
        props.multiSelect ? props.onSelectElementMulti(finalData) : props.onSelectElement(finalData);
    };

    useEffect(() => {
        if (dropdownOpen && wrapperRef.current) {
            const pickerRect = wrapperRef.current.getBoundingClientRect();
            const parentRect = props.parentRef?.current?.getBoundingClientRect();
            const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
            const hasCreateLine = !props.hideButtons && (inputValue?.length ?? 0) >= 1;
            if (props.fitResultsToAvailableSpace) {
                const clip = resolvePickerClipBounds(viewportHeight, parentRect || findPickerClippingAncestor(wrapperRef.current)?.getBoundingClientRect());
                const spaceBelow = clip.bottom - pickerRect.bottom;
                const upwards = shouldOpenPickerDropdownUpwards(
                    pickerRect.top,
                    pickerRect.bottom,
                    clip,
                    estimatePickerDropdownHeight(hasCreateLine, fitPickerResultsHeight(spaceBelow, hasCreateLine))
                );
                setOpenDropdownUpwards(upwards);
                setFittedResultsMaxHeight(fitPickerResultsHeight(upwards ? pickerRect.top - clip.top : spaceBelow, hasCreateLine));
                return;
            }
            const clip = resolvePickerClipBounds(viewportHeight, parentRect);
            setOpenDropdownUpwards(shouldOpenPickerDropdownUpwards(
                pickerRect.top,
                pickerRect.bottom,
                clip,
                estimatePickerDropdownHeight(hasCreateLine, props.resultsMaxHeight)
            ));
        }
    }, [dropdownOpen, inputValue, searchResults.length, props.hideButtons, props.resultsMaxHeight, props.fitResultsToAvailableSpace]);

    let pillColorSchema = props.pillColorSchema;
    if (!pillColorSchema) {
        pillColorSchema = props.multiSelect ? EPillColorSchema.BLUE : EPillColorSchema.TRANSPARENT;
    }

    const pillsList = selectedElements?.map((elem, index) => {
        const profileType = resolvePickerProfileType(elem?.data?.type);

        return (
            <Fragment key={elem?.key || index}>
                {elem &&
                    <>
                        {!profileType ? (
                            <Pill
                                key={elem.key}
                                text={getPickerPillText(elem)}
                                picture={getPickerPillPicture(elem)}
                                id={elem.key || elem.data?.id}
                                colorSchema={pillColorSchema}
                                onClick={(props.multiSelect) ? removeElement : undefined}
                                preventRedirection={true}
                            />
                        ) : (
                            <Pill
                                profileType={profileType}
                                key={elem?.key}
                                colorSchema={pillColorSchema}
                                shape={(profileType === EProfileType.Personal || profileType === EProfileType.Group) ? EPillShape.CIRCLE : EPillShape.SQUARE}
                                picture={getPickerPillPicture(elem)}
                                text={getPickerPillText(elem)}
                                id={elem.key || elem?.data?.id || elem?.data?.Id}
                                onClick={(props.multiSelect) ? removeElement : undefined}
                                preventRedirection={true}
                            />
                        )}
                    </>
                }
            </Fragment>
        );
    });

    const changeMainInputValue = (e: React.ChangeEvent<HTMLInputElement>) => {
        clearTimeout(debounceTimeout);
        openDropdown();
        setInputValue(e.target.value);
        debounceTimeout = setTimeout(() => {
            getData(e.target.value);
        }, 700);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Tab' && (!props.multiSelect || selectedElements?.length < 2)) {
            e.preventDefault();
        } else if (e.key === 'Tab' && props.multiSelect) {
            setExpand(true);
        }
    };

    return (
        <Wrapper className="Wrapper">
            {props.label &&
                <LabelWithEllipsis>
                    {props.label}
                    {props.required &&
                        <MandatoryFieldStar />
                    }
                </LabelWithEllipsis>
            }
            <WrapperContainer>
                <TextWrapper
                    selected={!!selectedElements}
                    multiSelect={props.multiSelect}
                    disabled={props.disabled}
                    expand={expand}
                    ref={wrapperRef}
                    onKeyDown={handleKeyDown}
                    error={!!props.errorMsg}
                    noIcon={wrapperRef?.current && wrapperRef?.current?.getBoundingClientRect().width < 400}
                    className="TextWrapper"
                    onClick={props.disabled ? undefined : onClick}
                >
                    {(wrapperRef?.current && wrapperRef?.current?.getBoundingClientRect().width > 400) &&
                        <SearchIcon>
                            <FontAwesomeIcon icon={faSearch} color={colorStack.darkGrey} />
                        </SearchIcon>
                    }
                    <ScrollBox expand={expand} numberOfLines={Array.from(selectedElemetsListInnerRef?.current?.children || [])?.filter((elem: any) => elem.offsetTop > 10)?.length} >
                        <Content expand={expand} disabled={props.disabled} noIcon={wrapperRef?.current && wrapperRef?.current?.getBoundingClientRect().width < 400}>
                            <SelectedElemsList
                                ref={selectedElemetsListRef}
                                disabled={props.disabled}
                                expand={expand}
                                leftIcon={wrapperRef?.current?.getBoundingClientRect().width > 400}
                            >
                                {!expand ? (
                                    <>
                                        <InnerBox multiSelect={props.multiSelect} ref={selectedElemetsListInnerRef} fullwidth={!(Array.from(selectedElemetsListInnerRef?.current?.children || [])?.filter((elem: any) => elem.offsetTop > 10)?.length > 1 && selectedElements?.length > 1)}>
                                            {pillsList}
                                            {(!props.disabled && !props.oldVersion && (props.multiSelect || selectedElements?.length === 0)) &&
                                                <InputObject disabled={props.disabled} type="text" value={inputValue} onChange={(showCompanyForm || showUserForm) ? undefined : changeMainInputValue} placeholder={props.placeholder || intlFormatMessage({ id: 'global.search' })} expand={expand} />
                                            }
                                        </InnerBox>
                                        {(Array.from(selectedElemetsListInnerRef?.current?.children || [])?.filter((elem: any) => elem.offsetTop > 10)?.length > 1 && reRender) &&
                                            <Badge
                                                color={EBadgeColor.GRAY}
                                                onClick={() => setExpand(state => !state)}
                                                placeholder={`${Array.from(selectedElemetsListInnerRef?.current?.children || [])?.filter((elem: any) => elem.offsetTop > 10)?.length - 1}+`}
                                            />
                                        }
                                    </>
                                ) : (
                                    <InnerBox ref={selectedElemetsListInnerRef} expand={expand} fullwidth multiSelect={props.multiSelect}>
                                        {pillsList}
                                        {(!props.disabled && !props.oldVersion && (props.multiSelect || selectedElements?.length === 0)) &&
                                            <InputObject disabled={props.disabled} type="text" value={inputValue} onChange={(showCompanyForm || showUserForm) ? undefined : changeMainInputValue} placeholder={props.placeholder || intlFormatMessage({ id: 'global.search' })} expand={expand} />
                                        }
                                    </InnerBox>

                                )}
                            </SelectedElemsList>
                        </Content>
                    </ScrollBox>
                    <RightIcons>
                        <FontAwesomeIcon icon={faXmark} color={colorStack.darkGrey} onClick={clearAllSelectedItems} />
                        <FontAwesomeIcon icon={faPipe} color={colorStack.ligthGrey} />
                        <FontAwesomeIcon icon={dropdownOpen ? faCaretUp : faCaretDown} color={colorStack.darkGrey} />
                    </RightIcons>
                </TextWrapper>
                {props.loading &&
                    <Spinner size={ESpinnerSize.SMALL} />
                }
            </WrapperContainer>
            {dropdownOpen &&
                <SearchResult className="SearchResult" openDropdownUpwards={openDropdownUpwards} data-nonclick="true">
                    {props.oldVersion ? (
                        <ResultBox
                            loading={loading}
                            placeholder={props.placeholder}
                            buttonsType={props.buttonsType}
                            hideButtons={props.hideButtons}
                            items={searchResults.map(elem => ({
                                text: elem.text,
                                id: elem.key,
                                visible: true,
                                data: elem.data,
                                translationKey: ''
                            }))}
                            multiSelect={props.multiSelect}
                            outsideGetSearchValue={setInputValue}
                            selectedItems={selectedItems}
                            onSelectItem={selectItemsHandler}
                            userFormHandler={userFormHandler}
                            companyFormHandler={companyFormHandler}
                            onClear={clearAllSelectedItems}
                            onSave={saveHandler}
                            type={EFilterType.personal}
                            translationKey=""
                            getData={getData}
                        />
                    ) : (
                        <SearchResults
                            loading={loading}
                            inputText={inputValue}
                            hideButtons={props.hideButtons}
                            selectedItems={selectedItems}
                            userFormHandler={userFormHandler}
                            companyFormHandler={companyFormHandler}
                            themeFormHandler={props.createTheme ? themeFormHandler : undefined}
                            clientReferenceFormHandler={props.createClientReference ? clientReferenceFormHandler : undefined}
                            areaOfResponsibilityFormHandler={props.createAreaOfResponsibility ? areaOfResponsibilityFormHandler : undefined}
                            onSelectItem={autoSaveSelectItemsHandler}
                            type={EFilterType.personal}
                            buttonsType={props.buttonsType}
                            multiSelect={props.multiSelect}
                            hasMore={props.enableInfiniteScroll && hasMoreResults}
                            onScrollResults={props.enableInfiniteScroll ? loadMoreResults : undefined}
                            getDisabledItemReason={props.getDisabledItemReason}
                            renderItemDetails={props.renderItemDetails}
                            renderItemAside={props.renderItemAside}
                            renderItemHeader={props.renderItemHeader}
                            resultsMaxHeight={(props.fitResultsToAvailableSpace && fittedResultsMaxHeight) || props.resultsMaxHeight}
                            items={searchResults.filter(result => !(selectedItems.map(item => item.id).includes(result.key))).map(elem => ({
                                text: elem.text,
                                id: elem.key,
                                visible: true,
                                data: elem.data,
                                translationKey: ''
                            }))}
                        />
                    )}

                </SearchResult>
            }
            <NewCompanyForm
                isVisible={showCompanyForm}
                isAddressMandatory={isAddressMandatory}
                closeHandler={companyFormHandler}
                initialData={{
                    name: inputValue
                }}
                returnData={selectElement}
                anchorRef={wrapperRef}
                anchorWidth={formAnchorWidth}
            />
            <NewUserForm
                allowEmptyEmail={allowEmptyEmail}
                onlyDataFromForm={props.onlyDataFromForm}
                isVisible={showUserForm}
                closeHandler={closeUserRofmHandler}
                initialData={(isValidEmail(inputValue) && !hrApiData) ? {
                    firstName: '',
                    lastName: '',
                    email: inputValue || ''
                } : {
                    firstName: hrApiData?.firstName ?? (inputValue?.split(/ (.*)/)?.[0] || ''),
                    lastName: hrApiData?.lastName ?? (inputValue?.split(/ (.*)/)?.[1] || ''),
                    email: hrApiData?.email ?? ''
                }}
                returnData={selectElement}
                anchorRef={wrapperRef}
                anchorWidth={formAnchorWidth}
            />
            {!!props.errorMsg &&
                <Hint type={EHintType.ERROR}>{props.errorMsg}</Hint>
            }
        </Wrapper>
    );
};
